// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Wifi, WifiOff, Menu, Leaf } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useOffline } from '@/hooks/useOffline'
import { useAuthStore } from '@/store'
import { ROUTES } from '@/lib/constants/routes'

interface TopBarProps {
  title?:           string
  showBack?:        boolean
  onBack?:          () => void
  showMenu?:        boolean
  onMenuToggle?:    () => void
  rightContent?:    React.ReactNode
  variant?:         'default' | 'auth' | 'nextofkin'
  className?:       string
}

export function TopBar({
  title,
  showBack,
  onBack,
  showMenu,
  onMenuToggle,
  rightContent,
  variant = 'default',
  className,
}: TopBarProps) {
  const navigate       = useNavigate()
  const { isOffline }  = useOffline()
  const { clearAuth }  = useAuthStore()

  function handleBack() {
    if (onBack) onBack()
    else navigate(-1)
  }

  if (variant === 'auth') {
    return (
      <header className={cn('flex items-center justify-center h-16 px-6', className)}>
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-moss">
            <Leaf className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-base font-semibold text-ink">WilsApp</span>
        </div>
      </header>
    )
  }

  if (variant === 'nextofkin') {
    return (
      <header className={cn(
        'flex items-center justify-between h-14 px-4',
        'border-b border-sand-200 bg-white',
        className,
      )}>
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-moss">
            <Leaf className="h-3 w-3 text-white" />
          </div>
          {title && (
            <span className="text-sm font-medium text-ink-3 truncate max-w-[200px]">
              {title}
            </span>
          )}
        </div>
        <button
          onClick={() => { clearAuth(); navigate(ROUTES.NEXT_OF_KIN_ENTRY) }}
          className="text-xs text-ink-4 hover:text-ink-3 transition-colors"
        >
          Sessie beëindigen
        </button>
      </header>
    )
  }

  // Default variant (desktop TopBar)
  return (
    <header
      className={cn(
        'flex items-center justify-between px-4 sm:px-6',
        'border-b border-sand-200 bg-white',
        className,
      )}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        minHeight: 'calc(56px + env(safe-area-inset-top))',
      }}
    >
      {/* Left */}
      <div className="flex items-center gap-3">
        {showMenu && (
          <button
            onClick={onMenuToggle}
            className="-ml-1 flex h-11 w-11 items-center justify-center rounded-lg text-ink-3 hover:bg-sand-100 transition-colors lg:hidden"
            aria-label="Menu openen"
          >
            <Menu className="h-6 w-6" />
          </button>
        )}
        {showBack && (
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm text-ink-3 hover:text-ink-2 transition-colors"
            aria-label="Terug"
          >
            <ArrowLeft className="h-4 w-4" />
            Terug
          </button>
        )}
        {title && (
          <h1 className="text-base font-semibold text-ink">{title}</h1>
        )}
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {isOffline && (
          <div
            className="flex items-center gap-1.5 rounded-full bg-status-warnBg border border-status-warnBorder px-2.5 py-1 text-xs text-status-warnText"
            title="U bent offline. Uw gegevens worden lokaal opgeslagen."
          >
            <WifiOff className="h-3 w-3" />
            <span className="hidden sm:inline">Offline</span>
          </div>
        )}
        {!isOffline && (
          <div className="hidden items-center gap-1 text-xs text-ink-4 md:flex" title="Online - gegevens veilig opgeslagen">
            <Wifi className="h-3 w-3" />
          </div>
        )}
        {rightContent}
      </div>
    </header>
  )
}
